import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import { useRunReportWizardStep } from '@/pages/RunReport/hooks/useRunReportWizardStep'
import {
  EConsentMethod,
  EReportType,
  ERunReportQueryParam,
  ERunReportStep,
} from '@/pages/RunReport/types'
import type { GooglePlace } from '@/pages/RunReport/utils/applyGooglePlaceToForm'
import { extractLocationParts } from '@/pages/RunReport/utils/applyGooglePlaceToForm'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import {
  setSocialMedia,
  setSubjectInfo,
} from '@/store/api/RunReport/RunReport.slice'
import type { subjectFormSchemaType } from '@/utils/schemas'
import { subjectFormSchema } from '@/utils/schemas'

import { ConsentRequestField } from '../../components/ConsentRequestField'
import { InputStrengthMeter } from '../../components/InputStrengthMeter'
import { ProfilePictureField } from '../../components/ProfilePictureField'
import { RunReportStepIndicator } from '../../components/RunReportStepIndicator'
import { SocialMediaAccountsField } from '../../components/SocialMediaAccountsField'
import { SubjectInfoFields } from '../../components/SubjectInfoFields'

const FIELDS_TO_COUNT: (keyof subjectFormSchemaType)[] = [
  'image',
  'firstName',
  'middleName',
  'lastName',
  'alias',
  'dob',
  'email',
  'countryLongName',
  'stateLongName',
  'cityLongName',
]

export const SubjectForm = () => {
  useRunReportGuard(ERunReportStep.SubjectForm)
  const { current, total } = useRunReportWizardStep(ERunReportStep.SubjectForm)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const slice = useAppSelector(selectRunReport)

  const form = useForm<subjectFormSchemaType>({
    resolver: zodResolver(subjectFormSchema),
    defaultValues: {
      image:
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null,
      firstName: slice.subjectInfo.firstName,
      middleName: slice.subjectInfo.middleName,
      lastName: slice.subjectInfo.lastName,
      alias: slice.subjectInfo.alias,
      dob: slice.subjectInfo.dob,
      email: slice.subjectInfo.email,
      countryLongName: slice.subjectInfo.countryLongName,
      stateLongName: slice.subjectInfo.stateLongName,
      cityLongName: slice.subjectInfo.cityLongName,
      socialMedia: slice.socialMedia,
    },
    mode: 'onChange',
  })

  const { handleSubmit, setValue, watch, trigger } = form

  const watchedValues = watch()
  const isSchemaValid = subjectFormSchema.safeParse(watchedValues).success

  const isDelegateFlow =
    slice.useCases.consentSwitchValue &&
    slice.consent.method === EConsentMethod.Delegate
  const isBusinessLocationValid =
    !isDelegateFlow || !!slice.consent.businessLocationValue
  const isEmailValid = !isDelegateFlow || !!watchedValues.email?.trim()
  const isFormValid = isSchemaValid && isBusinessLocationValid && isEmailValid

  const [countryShortName, setCountryShortName] = useState(
    slice.subjectInfo.countryShortName,
  )
  const [stateShortName, setStateShortName] = useState(
    slice.subjectInfo.stateShortName,
  )
  const [cityShortName, setCityShortName] = useState(
    slice.subjectInfo.cityShortName,
  )

  useEffect(() => {
    const formFields: Partial<Record<keyof subjectFormSchemaType, unknown>> = {
      image:
        typeof slice.subjectInfo.image === 'string'
          ? slice.subjectInfo.image
          : null,
      firstName: slice.subjectInfo.firstName,
      middleName: slice.subjectInfo.middleName,
      lastName: slice.subjectInfo.lastName,
      alias: slice.subjectInfo.alias,
      dob: slice.subjectInfo.dob,
      email: slice.subjectInfo.email,
      countryLongName: slice.subjectInfo.countryLongName,
      stateLongName: slice.subjectInfo.stateLongName,
      cityLongName: slice.subjectInfo.cityLongName,
      socialMedia: slice.socialMedia,
    }
    const hydratedKeys: (keyof subjectFormSchemaType)[] = []
    Object.entries(formFields).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        const typedKey = key as keyof subjectFormSchemaType
        setValue(typedKey, value as never, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        hydratedKeys.push(typedKey)
      }
    })
    if (hydratedKeys.length > 0) trigger(hydratedKeys)
  }, [slice.subjectInfo, slice.socialMedia, setValue, trigger])

  const handlePlaceSelected = (place: GooglePlace) => {
    const parts = extractLocationParts(place)
    setValue('countryLongName', parts.countryLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('stateLongName', parts.stateLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setValue('cityLongName', parts.cityLong, {
      shouldValidate: true,
      shouldDirty: true,
    })
    setCountryShortName(parts.countryShort)
    setStateShortName(parts.stateShort)
    setCityShortName(parts.cityShort)
  }

  const isAdverseMedia =
    slice.selectSearchType.reportType === EReportType.AdverseMediaReport

  const hasSocialMediaData =
    !isAdverseMedia &&
    Object.values(watch('socialMedia') ?? {}).some(
      (url) => (url ?? '').trim().length > 0,
    )

  const onSubmit = (data: subjectFormSchemaType) => {
    dispatch(
      setSubjectInfo({
        ...slice.subjectInfo,
        image: data.image,
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        alias: data.alias,
        dob: data.dob,
        email: data.email,
        countryLongName: data.countryLongName,
        countryShortName,
        stateLongName: data.stateLongName,
        stateShortName,
        cityLongName: data.cityLongName,
        cityShortName,
      }),
    )
    dispatch(setSocialMedia(data.socialMedia))
    navigate(AppRoutes.runReportAddExtraDetails)
  }

  const handleBack = () => {
    if (slice.selectSearchType.reportType === EReportType.AiReport) {
      const params = new URLSearchParams({
        [ERunReportQueryParam.Q]: slice.searchResultsInfo.searchInput,
        [ERunReportQueryParam.UseCaseId]: slice.useCases.selectedUseCaseId,
        [ERunReportQueryParam.Platform]: 'linkedin',
        [ERunReportQueryParam.ReportType]: slice.selectSearchType.reportType,
      })
      navigate(`${AppRoutes.runReportSearchResults}?${params.toString()}`)
      return
    }
    if (slice.useCases.consentSwitchValue) {
      navigate(AppRoutes.runReportConsentRequired)
      return
    }
    navigate(AppRoutes.runReportSearchForm)
  }

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      onClickBack={handleBack}
      hideHeaderOnMobile
      contentClassName="p-0"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex h-[calc(100dvh-3.5rem)] min-h-0 flex-col lg:h-full lg:flex-row lg:gap-6 lg:overflow-hidden lg:p-8"
        >
          <div className="hidden h-full flex-col lg:flex lg:flex-1">
            <div className="flex flex-1 flex-col justify-center gap-3 px-3">
              <RunReportStepIndicator current={current} total={total} />
              <h2 className="text-[32px] font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.subjectForm.title')}
              </h2>
              <p className="text-base text-neutralColor">
                {t('dashboard.runReport.subjectForm.subtitle')}
              </p>

              <div className="mt-2 flex gap-4">
                <Button type="button" variant="secondary" onClick={handleBack}>
                  {t('dashboard.runReport.subjectForm.back')}
                </Button>
                <Button type="submit" disabled={!isFormValid}>
                  {t('dashboard.runReport.subjectForm.continue')}
                </Button>
              </div>
            </div>

            <InputStrengthMeter
              form={form}
              fieldsToCount={FIELDS_TO_COUNT}
              hasSocialMediaData={hasSocialMediaData}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 lg:h-full lg:overflow-hidden lg:p-0">
            <div className="flex flex-col gap-5 rounded-2xl bg-baseColorHighlight p-4 lg:h-full lg:min-h-0 lg:flex-1 lg:gap-6 lg:overflow-y-auto lg:p-6">
              <ProfilePictureField
                hint={t('dashboard.runReport.subjectForm.profilePictureHint')}
              />

              <SubjectInfoFields
                onPlaceSelected={handlePlaceSelected}
                isEmailRequired={isDelegateFlow}
              />

              <ConsentRequestField />

              {!isAdverseMedia && (
                <>
                  <div className="border-t border-neutralColor5" />
                  <SocialMediaAccountsField />
                </>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-3 bg-baseColor px-4 pb-4 pt-3 lg:hidden">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-medium leading-tight text-neutralColor">
                {t('dashboard.runReport.subjectForm.title')}
              </h2>
              <p className="text-sm text-neutralColor">
                {t('dashboard.runReport.subjectForm.subtitle')}
              </p>
            </div>
            <div className="flex gap-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleBack}
                className="flex-1"
              >
                {t('dashboard.runReport.subjectForm.back')}
              </Button>
              <Button type="submit" disabled={!isFormValid} className="flex-1">
                {t('dashboard.runReport.subjectForm.continue')}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </WeScreen>
  )
}
